// src/customDataProvider.js
// Data provider custom pentru API Platform (JSON-LD/Hydra) cu React Admin 5.x
// Parsare nativa Hydra fara dependenta pe @api-platform/admin (incompatibil cu RA5)

import { fetchUtils, HttpError } from 'react-admin';

// In productie, REACT_APP_API_URL e injectat la build time (vezi
// Dockerfile.prod / docker-compose.prod.yml) - CRA citeste orice variabila
// prefixata cu REACT_APP_ si o "arde" direct in bundle-ul static la
// `npm run build`. Fallback-ul ramane neschimbat pentru dev local.
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// API Platform intoarce erorile de validare cu 422 si lista `violations`.
// React Admin marcheaza campurile din formular doar daca eroarea are forma
// { body: { errors: { camp: mesaj } } } (vezi useCreateController.save) -
// traducem violations in acea forma, altfel utilizatorul nu vede nimic.
const throwFieldErrors = (error) => {
    const violations = error?.body?.violations;
    if (Array.isArray(violations) && violations.length) {
        const errors = {};
        violations.forEach((v) => {
            if (v.propertyPath) errors[v.propertyPath] = v.message;
        });
        if (Object.keys(errors).length) {
            throw new HttpError('Verifica datele introduse', error.status || 422, {
                ...error.body,
                errors,
            });
        }
    }
    throw error;
};

const isTokenExpired = (token) => {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.exp < Math.floor(Date.now() / 1000);
    } catch (e) {
        return true;
    }
};

const getAuthHeaders = (extra = {}) => {
    const headers = new Headers(extra);
    headers.set('Accept', 'application/ld+json');
    const token = localStorage.getItem('token');
    if (token) {
        if (!isTokenExpired(token)) {
            headers.set('Authorization', `Bearer ${token}`);
        } else {
            localStorage.removeItem('token');
            localStorage.removeItem('roles');
            localStorage.removeItem('username');
            window.location.reload();
        }
    }
    return headers;
};

// Extrage id numeric din IRI sau integer
const extractNumericId = (value) => {
    if (!value) return value;
    if (typeof value === 'string') {
        const match = value.match(/\/(\d+)$/);
        return match ? parseInt(match[1], 10) : value;
    }
    return value;
};

// Normalizeaza un record Hydra: seteaza id numeric, pastreaza @id ca iri
const normalizeRecord = (record) => {
    if (!record) return record;
    return {
        ...record,
        id: extractNumericId(record['@id']) ?? record.id,
    };
};

export const customDataProvider = {
    getList: async (resource, params) => {
        const url = `${API_URL}/${resource}?_=${Date.now()}`;
        const { json } = await fetchUtils.fetchJson(url, {
            cache: 'no-store',
            headers: getAuthHeaders(),
        });
        const data = (json.member ?? json['hydra:member'] ?? json).map(normalizeRecord);
        const total = json.totalItems ?? json['hydra:totalItems'] ?? data.length;
        return { data, total };
    },

    getOne: async (resource, params) => {
        const { json } = await fetchUtils.fetchJson(
            `${API_URL}/${resource}/${params.id}?_=${Date.now()}`,
            { cache: 'no-store', headers: getAuthHeaders() }
        );
        return { data: normalizeRecord(json) };
    },

    getMany: async (resource, params) => {
        const results = await Promise.all(
            params.ids.map((id) =>
                fetchUtils.fetchJson(
                    `${API_URL}/${resource}/${extractNumericId(id)}?_=${Date.now()}`,
                    { cache: 'no-store', headers: getAuthHeaders() }
                ).then(({ json }) => normalizeRecord(json))
            )
        );
        return { data: results };
    },

    getManyReference: async (resource, params) => {
        const url = `${API_URL}/${resource}?_=${Date.now()}`;
        const { json } = await fetchUtils.fetchJson(url, {
            cache: 'no-store',
            headers: getAuthHeaders(),
        });
        const data = (json.member ?? json['hydra:member'] ?? json).map(normalizeRecord);
        const total = json.totalItems ?? json['hydra:totalItems'] ?? data.length;
        return { data, total };
    },

    create: async (resource, params) => {
        try {
            const { json } = await fetchUtils.fetchJson(`${API_URL}/${resource}`, {
                method: 'POST',
                body: JSON.stringify(params.data),
                headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
            });
            return { data: normalizeRecord(json) };
        } catch (error) {
            return throwFieldErrors(error);
        }
    },

    update: async (resource, params) => {
        // PATCH cu merge-patch+json pentru actualizari partiale
        // (drag&drop echipe, asociere colaboratori etc.)
        try {
            const { json } = await fetchUtils.fetchJson(
                `${API_URL}/${resource}/${params.id}`,
                {
                    method: 'PATCH',
                    body: JSON.stringify(params.data),
                    headers: getAuthHeaders({ 'Content-Type': 'application/merge-patch+json' }),
                }
            );
            return { data: normalizeRecord(json) };
        } catch (error) {
            return throwFieldErrors(error);
        }
    },

    updateMany: async (resource, params) => {
        await Promise.all(
            params.ids.map((id) =>
                fetchUtils.fetchJson(`${API_URL}/${resource}/${extractNumericId(id)}`, {
                    method: 'PATCH',
                    body: JSON.stringify(params.data),
                    headers: getAuthHeaders({ 'Content-Type': 'application/merge-patch+json' }),
                })
            )
        );
        return { data: params.ids };
    },

    delete: async (resource, params) => {
        await fetchUtils.fetchJson(`${API_URL}/${resource}/${params.id}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
        });
        return { data: params.previousData };
    },

    deleteMany: async (resource, params) => {
        await Promise.all(
            params.ids.map((id) =>
                fetchUtils.fetchJson(`${API_URL}/${resource}/${extractNumericId(id)}`, {
                    method: 'DELETE',
                    headers: getAuthHeaders(),
                })
            )
        );
        return { data: params.ids };
    },
};
